import { AsyncPipe, NgClass } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbAccordionBody, NgbAccordionButton, NgbAccordionCollapse, NgbAccordionDirective, NgbAccordionHeader, NgbAccordionItem, NgbAccordionToggle, NgbCollapse } from "@ng-bootstrap/ng-bootstrap";
import { FeatureKeys } from "company/components/features/feature.service";
import { PicklistConfigService } from "config/components/picklists/picklist-config.service";
import { cloneDeep, isEmpty, some } from "lodash";
import { FaIconDirective } from "rl-common/directives/fa-icon.directive";
import { FeatureEnabledDirective } from "rl-common/directives/feature-enabled.directive";
import { ICharacteristicMetaData } from "rl-common/models/i-characteristic-meta-data";
import { ICharacteristicTemplate } from "rl-common/models/i-characteristic-template";
import { ICharacteristicValue } from "rl-common/models/i-characteristic-value";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { CharDataType, CharTypeId, SystemIndicators } from "rl-common/rl-common.consts";
import { AccountingProcessesService } from "rl-common/services/accounting-processes/accounting-processes.service";
import { AllocationType } from "rl-common/services/accounting-processes/models/accounting-process-allocation-type";
import { DateGroupType } from "rl-common/services/accounting-processes/models/date-group-type";
import { IAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-accounting-process-account";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessDetails } from "rl-common/services/accounting-processes/models/i-accounting-process-details";
import { ICreateAccountingOperation } from "rl-common/services/accounting-processes/models/i-create-accounting-operation";
import { ICreateAccountingOperationEventAccountOrGroupMapping } from "rl-common/services/accounting-processes/models/i-create-accounting-operation-event-account-or-group-mapping";
import { ICreateDate } from "rl-common/services/accounting-processes/models/i-create-date";
import { ICreateEvent } from "rl-common/services/accounting-processes/models/i-create-event";
import { ICreateTrigger } from "rl-common/services/accounting-processes/models/i-create-trigger";
import { TriggerEventType } from "rl-common/services/accounting-processes/models/trigger-event-type";
import { AllocationService } from "rl-common/services/allocation/allocation.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { of, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, tap } from "rxjs/operators";
import { AccountingProcesses } from "../accounting-processes.consts";
import { IFfAccountingOperationForm } from "../accounting-processes.models";
import { AddAccountingOperationDatesComponent } from "../add-accounting-operation-dates/add-accounting-operation-dates.component";
import { AddAccountingOperationEventsComponent } from "../add-accounting-operation-events/add-accounting-operation-events.component";
import { AddAccountingOperationTriggersComponent } from "../add-accounting-operation-triggers/add-accounting-operation-triggers.component";
import { BuildAccountingProcessService } from "../build-accounting-process-modal/build-accounting-process.service";
import { IBuildAccountingOperationAccountOrGroup } from "./models/i-build-accounting-operation-account-or-group";
import { IBuildAccountingOperationDate } from "./models/i-build-accounting-operation-date";
import { IBuildAccountingOperationEvent } from "./models/i-build-accounting-operation-event";
import { IBuildAccountingOperationTrigger } from "./models/i-build-accounting-operation-trigger";

@Component({
	selector: "rl-ff-build-accounting-operation",
	templateUrl: "./ff-build-accounting-operation.component.html",
	styleUrls: ["./ff-build-accounting-operation.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, NgbAccordionDirective, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, NgClass, FaIconDirective, NgbCollapse, NgbAccordionCollapse, NgbAccordionBody, AddAccountingOperationTriggersComponent, AddAccountingOperationEventsComponent, FeatureEnabledDirective, AddAccountingOperationDatesComponent, AsyncPipe]
})
export class FfBuildAccountingOperationComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	accountingProcessId: string;

	@Input()
	accountingProcess: IAccountingProcessDetails;

	@Input()
	operation: ICreateAccountingOperation;

	@Input()
	accounts: IAccountingProcessAccount[] = [];

	@Input()
	accountGroups: IAccountingProcessAccountGroup[] = [];

	@Input()
	openPanels = false;

	@Output()
	anchorTemplateChange = new EventEmitter<ICharacteristicTemplate>();

	@Output()
	onSave = new EventEmitter<ICreateAccountingOperation>();

	@ViewChild("acc")
	accordion: NgbAccordionDirective;

	@ViewChild("anchorTemplateUpdateWarning", { read: TemplateRef })
	anchorTemplateUpdateWarning: TemplateRef<HTMLInputElement>;

	isNew = true;
	isSaved = false;
	workflowTrigger: IBuildAccountingOperationTrigger = null;
	selectedOperationType: number = null;
	operationTriggers: ICreateTrigger[] = [];

	form: FormGroup<IFfAccountingOperationForm>;
	formKey: string;

	initialPanelIds: string[] = ["add-triggers"];
	operationTypes = AccountingProcesses.operationTypes;
	featureKeys: typeof FeatureKeys = FeatureKeys;

	get triggersComplete() {
		const operationSaved = this.form.controls.id.value;
		const controls = this.form.controls.triggers.controls;
		const hasSavedTriggers = some(controls, control => !!control.value.id);
		return operationSaved && hasSavedTriggers;
	}

	get eventsComplete() {
		const operationSaved = this.form.controls.id.value;
		const controls = this.form.controls.events.controls;
		const hasSavedEvents = some(controls, control => !!control.value.id);
		return operationSaved && hasSavedEvents;
	}

	get datesComplete() {
		const operationSaved = this.form.controls.id.value;
		const controls = this.form.controls.dates.controls;
		const hasSavedDates = some(controls, control => !!control.value.accountingProcessOperationDateId && control.value.dateGroupTypeId === DateGroupType.FirstRecord);
		return operationSaved && hasSavedDates;
	}

	anchorTemplate: ICharacteristicTemplate;
	costAnchorTemplate: ICharacteristicTemplate;
	anchorTemplates: ICharacteristicTemplate[] = [];
	anchorTemplatesRevenue: ICharacteristicTemplate[] = [];
	anchorTemplatesCost: ICharacteristicTemplate[] = [];
	anchorTemplateDates: ICharacteristicMetaData[] = [];
	anchorTemplatesDates: { [key: number]: ICharacteristicMetaData[] } = {};
	datesDictionary: { [key: number]: string } = {};
	initialAnchorTemplateId: number;
	initialCostAnchorTemplateId: number;
	isInitialized: boolean;
	revenueAllocationTemplateId: number;
	costAllocationTemplateId: number;

	feeTypeItems: ICharacteristicValue[] = [];
	readonly defaultFeeType = { charValueID: 0, charValueLabel: "None" } as ICharacteristicValue;

	isSaving$ = this._buildAccountingProcessService.status$.pipe(map(x => x.isSaving));

	private _formSubs: Subscription[] = [];
	private readonly _subs: Subscription[] = [];

	constructor(
		private readonly _formBuilder: FormBuilder,
		private readonly _accountingProcessesService: AccountingProcessesService,
		private readonly _buildAccountingProcessService: BuildAccountingProcessService,
		private readonly _oneConfig: OneConfigService,
		private readonly _promptService: PromptService,
		private readonly _allocationService: AllocationService,
		private readonly _picklistConfigService: PicklistConfigService
	) { }

	async ngOnInit(): Promise<void> {
		this.isInitialized = true;
		this.isNew = !this.operation;
		if (!this.isNew) {
			if (this.openPanels) {
				this.initialPanelIds = ["add-triggers", "add-events"];
			} else {
				this.initialPanelIds = [];
			}
		}
		const addAnotherOpSub = this._buildAccountingProcessService.addAnotherOperation$.subscribe(() => {
			this.reset();
		});
		this.operationTriggers = this.operation?.triggers;

		await this.setFeeTypeItems();

		const allocSub = this._allocationService.getAllocationTemplateConfiguration().subscribe(result => {
			this.revenueAllocationTemplateId = result.revenueAllocationTemplateId;
			this.costAllocationTemplateId = result.costAllocationTemplateId;
			this.updateAnchorTemplatesList();

			this.anchorTemplates.forEach(t => {
				const templateCmds = this._oneConfig.getTemplateCmds(CharTypeId.Amount, t.templateID);
				this.anchorTemplatesDates[t.templateID] = templateCmds.filter(cmd => cmd.dataTypeID === CharDataType.Date);
			});

			this.buildForm(this.operation);
		});

		this._subs.push(allocSub, addAnotherOpSub);
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if (!this.isInitialized) return;

		if (changes.anchorTemplate) {
			this.onAnchorTemplateChange(changes.anchorTemplate.currentValue);
		}

		if (changes.costAnchorTemplate) {
			this.onCostAnchorTemplateChange(changes.costAnchorTemplate.currentValue);
		}

		if (changes.operation) {
			this.buildForm(this.operation);
		}

		if (changes.accountingProcess) {
			this.updateAnchorTemplatesList();
		}
	}

	async setFeeTypeItems() {
		this.feeTypeItems = [];
		const feeType = this._oneConfig.getCharBySystemIndicator(CharTypeId.Amount, SystemIndicators.FeeType);
		if (feeType) {
			this.feeTypeItems = cloneDeep(await this._picklistConfigService.getPicklistSourceValues(feeType.charValueSetID).toPromise());
			this.feeTypeItems.unshift(this.defaultFeeType);
		}
	}

	updateAnchorTemplatesList() {
		const usedRevenueTemplates = this.accountingProcess?.operations.filter(x => x.id != this.operation?.id).map(x => x.anchorTemplateId) ?? [];
		const usedCostTemplates = this.accountingProcess?.operations.filter(x => x.id != this.operation?.id).map(x => x.costAnchorTemplateId) ?? [];
		const usedOnOtherOperationTemplateIds = [...usedRevenueTemplates, ...usedCostTemplates];

		this.anchorTemplates = this._oneConfig.getTemplates(CharTypeId.Amount).filter(x => x.systemIndicator === SystemIndicators.AllocationTemplate);

		this.anchorTemplatesRevenue = this.anchorTemplates
			.filter(x => usedOnOtherOperationTemplateIds.indexOf(x.templateID) < 0 && x.templateID !== this.costAllocationTemplateId && x.templateID !== this.costAnchorTemplate?.templateID);
		this.anchorTemplatesCost = this.anchorTemplates
			.filter(x => usedOnOtherOperationTemplateIds.indexOf(x.templateID) < 0 && x.templateID !== this.revenueAllocationTemplateId && x.templateID !== this.anchorTemplate?.templateID);

		if (!this.form) return;
		if (!this.anchorTemplatesRevenue.find(x => x.templateID == this.form.controls.anchorTemplateId.value)) {
			this.initialAnchorTemplateId = null;
			this.form.controls.anchorTemplateId.patchValue(null);
			this.form.controls.anchorTemplateId.markAsTouched();
		}
		if (this.costAllocationTemplateId !== -1 && !this.anchorTemplatesCost.find(x => x.templateID == this.form.controls.costAnchorTemplateId.value)) {
			this.initialCostAnchorTemplateId = null;
			this.form.controls.costAnchorTemplateId.patchValue(null);
			this.form.controls.costAnchorTemplateId.markAsTouched();
		}
	}

	updateAnchorTemplate(templateId: number) {
		const newAnchorTemplate = this.anchorTemplates.find(x => x.templateID == templateId);
		this.anchorTemplate = newAnchorTemplate;
		this.updateAnchorTemplatesList();

		if (newAnchorTemplate) {
			this.onAnchorTemplateChange(newAnchorTemplate);
		}
	}

	updateCostAnchorTemplate(templateId: number) {
		const newCostAnchorTemplate = this.anchorTemplates.find(x => x.templateID == templateId);
		this.costAnchorTemplate = newCostAnchorTemplate;
		this.updateAnchorTemplatesList();

		if (newCostAnchorTemplate) {
			this.onCostAnchorTemplateChange(newCostAnchorTemplate);
		}
	}

	onAnchorTemplateChange(template: ICharacteristicTemplate) {
		if (this.initialAnchorTemplateId == template.templateID) {
			this.form.controls.anchorTemplateId.markAsPristine();
		}
	}

	onCostAnchorTemplateChange(template: ICharacteristicTemplate) {
		if (this.initialCostAnchorTemplateId == template.templateID) {
			this.form.controls.costAnchorTemplateId.markAsPristine();
		}
	}

	buildForm(operation: ICreateAccountingOperation = null) {
		this._formSubs.forEach(sub => sub.unsubscribe());

		if (this.formKey) {
			this._buildAccountingProcessService.unregisterForm(this.formKey);
		}

		// if a template was configured which no longer has the system indicator
		if (operation && !this.anchorTemplates.find(x => x.templateID == operation?.anchorTemplateId)) {
			operation.anchorTemplateId = null;
		}

		// Normalize costAnchorTemplateId: backend persists 0 for "None", but the dropdown uses null
		const normalizedCostAnchorTemplateId = operation?.costAnchorTemplateId > 0 ? operation.costAnchorTemplateId : null;
		this.form = this._formBuilder.group({
			id: new FormControl<string>(operation?.id),
			name: new FormControl<string>(operation?.operationName, [Validators.required]),
			anchorTemplateId: new FormControl<number>(operation?.anchorTemplateId, [Validators.required]),
			costAnchorTemplateId: new FormControl<number>(normalizedCostAnchorTemplateId),
			operationType: new FormControl<number>(operation?.operationType ?? AccountingProcesses.OperationType.None),
			triggers: new FormArray<FormControl<IBuildAccountingOperationTrigger>>(this.mapTriggers(operation?.triggers)),
			events: new FormArray<FormControl<IBuildAccountingOperationEvent>>(this.mapEvents(operation?.events)),
			dates: new FormArray<FormControl<IBuildAccountingOperationDate>>(this.mapDates(operation?.dates))
		});

		this.workflowTrigger = this.form.controls.triggers.controls.find(control => control.value.triggerType === TriggerEventType.WorkflowAction)?.value;

		this.selectedOperationType = this.form.controls.operationType.value;

		const triggersChangedSub = this.form.controls.triggers.valueChanges.subscribe((triggers) => {
			this.saveAccountingOperation();
			this.workflowTrigger = triggers.find(x => x.triggerType === TriggerEventType.WorkflowAction);
			this.operationTriggers = this.mapOperationTriggers(triggers);
		});

		const eventsChangedSub = this.form.controls.events.valueChanges.subscribe(() => {
			this.saveAccountingOperation();
		});

		const datesChangedSub = this.form.controls.dates.valueChanges.subscribe(() => {
			this.saveAccountingOperation();
		});

		this.initialAnchorTemplateId = operation?.anchorTemplateId > 0 ? operation?.anchorTemplateId : null;
		this.initialCostAnchorTemplateId = operation?.costAnchorTemplateId > 0 ? operation?.costAnchorTemplateId : null;

		if (this.anchorTemplates.length == 0) {
			this.form.controls.anchorTemplateId.disable();
			this.form.controls.costAnchorTemplateId.disable();
		}

		if (operation?.anchorTemplateId == null) {
			this.form.controls.anchorTemplateId.markAsTouched();
		}

		if (operation?.costAnchorTemplateId == null) {
			this.form.controls.costAnchorTemplateId.markAsTouched();
		}

		this.updateAnchorTemplate(operation?.anchorTemplateId);
		this.updateCostAnchorTemplate(operation?.costAnchorTemplateId);

		const anchorTemplateSub = this.form.controls.anchorTemplateId.valueChanges.pipe(
			distinctUntilChanged()
		).subscribe(anchorTemplateId => {
			this.updateAnchorTemplate(anchorTemplateId);
		});

		const costAnchorTemplateSub = this.form.controls.costAnchorTemplateId.valueChanges.pipe(
			distinctUntilChanged()
		).subscribe(anchorTemplateId => {
			this.updateCostAnchorTemplate(anchorTemplateId);
		});

		this.formKey = `BuildAccountingOperationComponent-${operation?.id ?? "0"}`;
		this._buildAccountingProcessService.registerForm(this.formKey, this.form);

		this._formSubs = [triggersChangedSub, eventsChangedSub, datesChangedSub, anchorTemplateSub, costAnchorTemplateSub];
	}

	private mapTriggers(triggers: ICreateTrigger[]): FormControl<IBuildAccountingOperationTrigger>[] {
		if (!triggers) {
			return [];
		}
		return triggers.map(x => {
			const trigger = {
				id: x.id,
				triggerType: x.triggerType,
				actionId: x.actionId,
				charTypeId: x.charTypeId,
				templateId: x.templateId,
				characteristicId: x.characteristicId,
				operator: x.operator,
				value: x.value,
				rangeValue1: x.rangeValue1,
				rangeValue2: x.rangeValue2
			} as IBuildAccountingOperationTrigger;
			return new FormControl<IBuildAccountingOperationTrigger>(trigger);
		});
	}

	private mapOperationTriggers(triggers: IBuildAccountingOperationTrigger[]) {
		if (!triggers) {
			return [];
		}
		return triggers.map(x => {
			return {
				id: x.id,
				triggerType: x.triggerType,
				actionId: x.actionId,
				charTypeId: x.charTypeId,
				templateId: x.templateId,
				characteristicId: x.characteristicId,
				operator: x.operator,
				value: x.value,
				rangeValue1: x.rangeValue1,
				rangeValue2: x.rangeValue2
			} as ICreateTrigger;
		});
	}

	private mapEvents(events: ICreateEvent[]): FormControl<IBuildAccountingOperationEvent>[] {
		if (!events) {
			return [];
		}
		return events.map(x => {
			const accounts = x.accountOrGroups.map(account => {
				const a: IBuildAccountingOperationAccountOrGroup = {
					debitId: account.debitId,
					creditId: account.creditId,
					debitName: account.debitName,
					creditName: account.creditName,
					allocationType: account.allocationType,
					feeType: account.feeType,
					feeTypeCharVal: this.getFeeTypeCharVal(account.feeType)
				} as IBuildAccountingOperationAccountOrGroup;
				return a;
			});
			const event = {
				id: x.id,
				statusId: x.statusId,
				workflowActionId: x.workflowActionId,
				accountOrGroups: accounts
			} as IBuildAccountingOperationEvent;
			return new FormControl<IBuildAccountingOperationEvent>(event);
		});
	}

	getFeeTypeCharVal(id: number) {
		return this.feeTypeItems.find(f => f.charValueID === id);
	}

	private mapDates(dates: ICreateDate[]): FormControl<IBuildAccountingOperationDate>[] {
		if (!dates) {
			return [];
		}
		return dates.map(x => {
			const date = {
				accountingProcessOperationDateId: x.accountingProcessOperationDateId,
				dateGroupTypeId: x.dateGroupTypeId,
				dateTypeId: x.dateTypeId,
				characteristicId: x.characteristicId,
				characteristicIdDefault1: x.characteristicIdDefault1,
				populateTypeId: x.populateTypeId
			} as IBuildAccountingOperationDate
			return new FormControl<IBuildAccountingOperationDate>(date);
		});
	}

	saveAccountingOperation() {
		if (!this.form.valid || this._buildAccountingProcessService.status$.value.isSaving) {
			return;
		}

		// Defer save if any saved events are missing revenue accounts
		// Backend will reject with "Each event requires an account or account group."
		const savedEventsMissingRevAccounts = this.form.controls.events.controls.some(control => {
			const event = control.value;
			if (!event.id) {
				return false;
			}
			return !event.accountOrGroups?.some(x =>
				x.allocationType === AllocationType.Revenue &&
				x.debitId?.id != null &&
				x.creditId?.id != null
			);
		});
		if (savedEventsMissingRevAccounts) {
			return;
		}

		// If cost allocation template is selected, defer save until all events have valid cost accounts
		// Otherwise backend will reject with "Cost Allocation Debit & Credit selections are required."
		const hasCostAllocationTemplate = !!this.form.controls.costAnchorTemplateId.value;
		if (hasCostAllocationTemplate) {
			const savedEventsMissingCostAccounts = this.form.controls.events.controls.some(control => {
				const event = control.value;
				if (!event.id) {
					return false;
				}
				return !event.accountOrGroups?.some(x =>
					x.allocationType === AllocationType.Cost &&
					x.debitId?.id != null &&
					x.creditId?.id != null
				);
			});
			if (savedEventsMissingCostAccounts) {
				return;
			}
		}

		const triggers = this.form.controls.triggers.controls
			.filter(control => !isEmpty(control.value))
			.map(triggerControl => {
				const trigger = triggerControl.value;
				const createTrigger: ICreateTrigger = {
					id: trigger.id,
					accountProcessOperationId: this.form.controls.id.value,
					triggerType: trigger.triggerType,
					actionId: trigger.actionId,
					charTypeId: trigger.charTypeId,
					templateId: trigger.templateId,
					characteristicId: trigger.characteristicId,
					operator: trigger.operator,
					value: trigger.value,
					rangeValue1: trigger.rangeValue1,
					rangeValue2: trigger.rangeValue2,
					amountTemplateId: trigger.amountTemplateId,
					tableTemplateId: trigger.tableTemplateId
				};
				return createTrigger;
			});
		const events = this.form.controls.events.controls
			.filter(control => !isEmpty(control.value))
			.map(eventControl => {
				const event = eventControl.value;
				// Filter out accounts with null/invalid debit/credit selections,
				// and drop cost accounts entirely if no cost allocation template is selected
				const accountOrGroups = (event.accountOrGroups ?? [])
					.filter(x => {
						if (x.debitId?.id == null || x.creditId?.id == null) {
							return false;
						}
						if (x.allocationType === AllocationType.Cost) {
							return hasCostAllocationTemplate;
						}
						return true;
					})
					.map(x => ({
						debitId: x.debitId,
						creditId: x.creditId,
						debitName: x.debitName,
						creditName: x.creditName,
						allocationType: x.allocationType,
						feeType: x.feeType
					} as ICreateAccountingOperationEventAccountOrGroupMapping));
				const createEvent: ICreateEvent = {
					id: event.id,
					accountProcessOperationId: this.form.controls.id.value,
					statusId: event.statusId,
					workflowActionId: event.workflowActionId,
					accountOrGroups
				};
				return createEvent;
			});
		const dates = this.form.controls.dates.controls
			.filter(control => !isEmpty(control.value))
			.map(dateControl => {
				const date = dateControl.value;
				const createDate: ICreateDate = {
					accountingProcessOperationDateId: date.accountingProcessOperationDateId,
					accountProcessOperationId: this.form.controls.id.value,
					dateGroupTypeId: date.dateGroupTypeId,
					dateTypeId: date.dateTypeId,
					characteristicId: date.characteristicId,
					characteristicIdDefault1: date.characteristicIdDefault1,
					populateTypeId: date.populateTypeId
				};
				return createDate;
			})

		const anchorTemplateChanged = this.initialAnchorTemplateId !== this.form.controls.anchorTemplateId.value;
		let clearTriggers$ = of(true);
		if (anchorTemplateChanged) {
			if ((triggers.length > 0 || events.length > 0 || dates.length > 0)) {
				clearTriggers$ = this._promptService.confirmMini(`Warning`, this.anchorTemplateUpdateWarning);
			}

			const template = this._oneConfig.getTemplate(CharTypeId.Amount, this.form.controls.anchorTemplateId.value);
			this.anchorTemplateChange.emit(template);
		}

		const operation: ICreateAccountingOperation = {
			accountingProcessId: this.accountingProcessId,
			id: this.form.controls.id.value,
			operationName: this.form.controls.name.value,
			anchorTemplateId: this.form.controls.anchorTemplateId.value,
			costAnchorTemplateId: this.form.controls.costAnchorTemplateId.value,
			operationType: this.form.controls.operationType.value,
			triggers,
			events,
			dates,
			clearTriggers: anchorTemplateChanged
		};
		const sub = clearTriggers$.pipe(
			filter((response) => response),
			tap(() => this._buildAccountingProcessService.emitStatus({ isSaving: true, message: `Saving ...` })),
			switchMap(() => this._accountingProcessesService.upsertAccountingOperation(operation)),
			tap(() => this._buildAccountingProcessService.emitStatus({ isSaving: false, message: `Saved` })),
		).subscribe({
			next: (result) => {
				this.buildForm(result);
				this.onSave.emit(result)
				this.isSaved = true;
			},
			error: () => {
				// Reset saving status on error so subsequent saves can proceed
				this._buildAccountingProcessService.emitStatus({ isSaving: false, message: `` });
			}
		});
		this._subs.push(sub);
	}

	canSave() {
		if (!this.form.valid || !this.form.dirty) {
			return false;
		}

		// Every event must have valid revenue accounts
		const allEventsHaveRevAccounts = this.form.controls.events.controls.every(control => {
			const event = control.value;
			return event.accountOrGroups?.some(x =>
				x.allocationType === AllocationType.Revenue &&
				x.debitId?.id != null &&
				x.creditId?.id != null
			);
		});
		if (!allEventsHaveRevAccounts) {
			return false;
		}

		// If cost allocation template is selected, ensure all events have valid cost accounts
		const hasCostAllocationTemplate = !!this.form.controls.costAnchorTemplateId.value;
		if (hasCostAllocationTemplate) {
			const allEventsHaveCostAccounts = this.form.controls.events.controls.every(control => {
				const event = control.value;
				return event.accountOrGroups?.some(x =>
					x.allocationType === AllocationType.Cost &&
					x.debitId?.id != null &&
					x.creditId?.id != null
				);
			});

			if (!allEventsHaveCostAccounts) {
				return false;
			}
		}

		return true;
	}

	showRevAccountsRequiredWarning(): boolean {
		// Show warning if operation is saved but events are missing revenue accounts
		if (!this.form?.controls.id.value) {
			return false;
		}

		return this.form.controls.events.controls.some(control => {
			const event = control.value;
			const hasRevAccounts = event.accountOrGroups?.some(x =>
				x.allocationType === AllocationType.Revenue &&
				x.debitId?.id != null &&
				x.creditId?.id != null
			);
			return !hasRevAccounts;
		});
	}

	showCostAccountsRequiredWarning(): boolean {
		// Show warning if cost template is selected and operation is saved, but events are missing cost accounts
		if (!this.form?.controls.id.value || !this.form?.controls.costAnchorTemplateId.value) {
			return false;
		}

		const hasEventMissingCostAccounts = this.form.controls.events.controls.some(control => {
			const event = control.value;
			const hasCostAccounts = event.accountOrGroups?.some(x =>
				x.allocationType === AllocationType.Cost &&
				x.debitId?.id != null &&
				x.creditId?.id != null
			);
			return !hasCostAccounts;
		});

		return hasEventMissingCostAccounts;
	}

	reset() {
		this.operation = null;
		this.buildForm();
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
		this._formSubs.forEach(sub => sub.unsubscribe());
		if (this.formKey) {
			this._buildAccountingProcessService.unregisterForm(this.formKey);
		}
	}
}
